import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Institute } from '../types/institute';
import { environment } from '../../environments/environment';
import { Observable, tap } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

/**
 * Centralized service for institute management with signal-based state.
 *
 * State management:
 * - `institutes` signal holds all institutes sorted by name
 * - All CRUD operations automatically refresh the institutes list
 * - Components consume the signal reactively via computed()
 */
@Injectable({
  providedIn: 'root'
})
export class InstituteServices {
  private readonly API_URL = environment.apiUrl + '/rest/service-point';

  private readonly http = inject(HttpClient);
  private readonly toastr = inject(ToastrService);

  readonly institutes = signal<Institute[]>([]);
  readonly filteredInstitutes = signal<Institute[]>([]);
  readonly isLoading = signal<boolean>(false);

  loadInstitutes(): void {
    this.isLoading.set(true);
    this.http.get<Institute[]>(`${this.API_URL}/`).subscribe({
      next: (institutes) => {
        const sortedInstitutes = this.institutesSort(institutes ?? []);
        this.institutes.set(sortedInstitutes);
        // filter lab from institutes (ekatEventType == 'LABOR')
        this.filteredInstitutes.set(sortedInstitutes.filter(institute => institute.ekatEventType?.toLowerCase() !== 'patologia'));
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        this.toastr.error('Intézmények betöltése sikertelen');
      }
    });
  }

  institutesSort(institutes: Institute[]): Institute[] {
    return institutes.sort((a, b) => a.name.localeCompare(b.name));
  }

  addInstitute(institute: Institute): Observable<Institute> {
    return this.http.post<Institute>(`${this.API_URL}/save`, institute).pipe(
      tap(() => this.loadInstitutes())
    );
  }

  updateInstitute(institute: Institute): Observable<Institute> {
    return this.http.put<Institute>(`${this.API_URL}/modify/${institute.id}`, institute).pipe(
      tap(() => this.loadInstitutes())
    );
  }

  deleteInstitute(instituteId: number): Observable<void> {
    return this.http.delete<void>(`${this.API_URL}/delete/${instituteId}`).pipe(
      tap(() => this.loadInstitutes())
    );
  }

  getInstituteById(instituteId: number): Observable<Institute> {
    return this.http.get<Institute>(`${this.API_URL}/${instituteId}`);
  }
}
